import IRatingAgenciesMap from '@/creditflowresearch/domain/vo/dictionaries/IRatingAgenciesMap';
import { IRatingAgency } from '@/creditflowresearch/domain/vo/RatingAgency/IRatingAgency';
import {
  ABS_PRODUCT_ID,
  HYC_PRODUCT_ID,
  IGC_PRODUCT_ID,
} from '@/creditflowresearch/domain/Product/constants';
import {
  APAC_REGION_ID,
  EMEA_REGION_ID,
  LATM_REGION_ID,
  USOA_REGION_ID,
} from '@/creditflowresearch/domain/Region/constants';

const AgencyIds = {
  Moodys: '319286e9-b15c-476b-b265-bf3c33592cc4',
  SAndP: 'c30be82f-1530-423d-a1c6-b1dc11214f94',
  Fitch: '6659ca2f-d217-42b6-ae16-e047ba430439',
  DBRS: '1f435932-7d91-4c8d-9369-c040327aaf9b',
  Kroll: '2417fb0f-db90-48a2-8b2e-2ea980a3e215',
  Morningstar: 'b2041576-c507-47b5-8c08-b4fddba65a64',
  EulerHermes: 'a88c2cb5-a584-451b-b527-3a919717e069',
  Fedafin: 'd904a737-c0d9-4375-a2c5-5d938e0fd4ca',
  Scope: '527b14ad-a298-48e1-9ab4-f8dce4e4ab88',
  ChinaDagong: '45333912-71fb-428c-9036-473018e35664',
  ChinaLianhe: 'fe1b2d0b-efe5-42b7-843d-f65f6ac447f3',
  ChinaPengyuan: '4ec0ebcf-d470-433d-bf45-4b443dd20a08',
  ChinaChengxin: 'bf595c2f-db89-45f6-86b2-0ba8fc05e224',
  JCR: '5d600ade-3c45-4a3f-a287-e5180bfa57fd',
  Marc: 'f7e1f7cb-f4a4-4ae8-9722-545afe5aa4ca',
  RI: '1d953c9f-6906-46e2-9df1-174adafb9e2d',
  RAM: 'dc970491-f6c4-4478-b5c9-7bd51f240bf7',
  Tris: '87a19f3f-1122-4b14-be73-0e979ffea7bc',
} as const;

// [id, priority, isAlwaysDisplay]
type AgencyConfig = [string, number, boolean];

// common region configs
const EMEA_CONFIG: AgencyConfig[] = [
  [AgencyIds.Moodys, 1, true],
  [AgencyIds.SAndP, 2, true],
  [AgencyIds.Fitch, 3, true],
  [AgencyIds.DBRS, 5, false],
  [AgencyIds.EulerHermes, 6, false],
  [AgencyIds.Fedafin, 7, false],
  [AgencyIds.Kroll, 8, false],
  [AgencyIds.Morningstar, 9, false],
  [AgencyIds.Scope, 10, false],
];

const USOA_CONFIG: AgencyConfig[] = [
  [AgencyIds.Moodys, 1, true],
  [AgencyIds.SAndP, 2, true],
  [AgencyIds.Fitch, 3, true],
  [AgencyIds.DBRS, 5, false],
  [AgencyIds.Kroll, 6, false],
  [AgencyIds.Morningstar, 7, false],
];

const USOA_ABS_CONFIG: AgencyConfig[] = [
  [AgencyIds.Moodys, 1, true],
  [AgencyIds.SAndP, 2, true],
  [AgencyIds.Fitch, 3, true],
  [AgencyIds.DBRS, 4, true],
  [AgencyIds.Kroll, 5, true],
  [AgencyIds.Morningstar, 6, true],
];

const LATM_CONFIG: AgencyConfig[] = [
  [AgencyIds.Moodys, 1, true],
  [AgencyIds.SAndP, 2, true],
  [AgencyIds.Fitch, 3, true],
  [AgencyIds.DBRS, 5, false],
  [AgencyIds.Kroll, 6, false],
  [AgencyIds.Morningstar, 7, false],
];

const APAC_CONFIG: AgencyConfig[] = [
  [AgencyIds.Moodys, 1, true],
  [AgencyIds.SAndP, 2, true],
  [AgencyIds.Fitch, 3, true],
  [AgencyIds.ChinaDagong, 5, false],
  [AgencyIds.ChinaLianhe, 6, false],
  [AgencyIds.ChinaPengyuan, 7, false],
  [AgencyIds.ChinaChengxin, 8, false],
  [AgencyIds.DBRS, 9, false],
  [AgencyIds.EulerHermes, 10, false],
  [AgencyIds.Fedafin, 11, false],
  [AgencyIds.JCR, 12, false],
  [AgencyIds.Kroll, 13, false],
  [AgencyIds.Marc, 14, false],
  [AgencyIds.Morningstar, 15, false],
  [AgencyIds.RI, 16, false],
  [AgencyIds.RAM, 17, false],
  [AgencyIds.Scope, 18, false],
  [AgencyIds.Tris, 19, false],
];

const MAPPING: Record<string, Record<string, AgencyConfig[]>> = {
  [IGC_PRODUCT_ID]: {
    [EMEA_REGION_ID]: EMEA_CONFIG,
    [USOA_REGION_ID]: USOA_CONFIG,
    [LATM_REGION_ID]: LATM_CONFIG,
    [APAC_REGION_ID]: APAC_CONFIG,
  },
  [HYC_PRODUCT_ID]: {
    [EMEA_REGION_ID]: EMEA_CONFIG,
    [USOA_REGION_ID]: USOA_CONFIG,
    [LATM_REGION_ID]: LATM_CONFIG,
    [APAC_REGION_ID]: APAC_CONFIG,
  },
  [ABS_PRODUCT_ID]: {
    [EMEA_REGION_ID]: EMEA_CONFIG,
    [USOA_REGION_ID]: USOA_ABS_CONFIG,
    [LATM_REGION_ID]: LATM_CONFIG,
    [APAC_REGION_ID]: APAC_CONFIG,
  },
};

const RatingAgenciesMappingService = {
  buildMap(ratingAgencies: Omit<IRatingAgency, 'ratings'>[]): IRatingAgenciesMap {
    const byId = new Map(ratingAgencies.map((a) => [a.id, a]));
    const result: IRatingAgenciesMap = {};

    for (const [productId, regions] of Object.entries(MAPPING)) {
      result[productId] = {};
      for (const [regionId, configs] of Object.entries(regions)) {
        result[productId][regionId] = configs
          .map(([id, priority, isAlwaysDisplay]) => {
            const agency = byId.get(id);
            if (!agency) return null;
            return {
              id,
              name: agency.name,
              displayName: agency.displayName,
              abbreviation: agency.abbreviation,
              priority,
              isAlwaysDisplay,
            };
          })
          .filter(Boolean);
      }
    }
    return result;
  },
};

export default RatingAgenciesMappingService;
